<div [formGroup]="formGroup" *ngIf="formGroup">

  <!-- Business Income -->
  <div class="row mt-2 mb-1">
    <div class="col-12" *ngIf="!isCompany">
      <strong>Personal Income Derived from Business</strong>
      <mat-icon class="ms-2" matTooltip="Enter the declared personal income for each applicant. Note: When completing the BAS fields below this should be entered as per the Business Activity Statement. Do not deduct the declared personal income from the Director(s) / Applicant(s) Salaries.">info</mat-icon>
    </div>
    <div class="col-12" *ngIf="isCompany">
      <strong>Income - Net Profit Before Tax</strong>
      <small class="text-muted d-block">(if loss, show negative)</small>
    </div>
  </div>
  <div class="row mb-3">
    <div class="col-6">
      <mat-form-field appearance="outline" class="w-100">
        <mat-label>Income amount</mat-label>
        <input matInput matInputCommified formControlName="businessIncome" placeholder="Income amount">
        <span matPrefix>$&nbsp;</span>
        <mat-error>
          <app-validation-partial [formGroup]="formGroup" [fieldName]="'businessIncome'"></app-validation-partial>
        </mat-error>
      </mat-form-field>
    </div>
  </div>
  <hr class="mb-3">

  <!-- Controls row -->
  <div class="row align-items-start">
    <div class="col-4">
      <mat-form-field appearance="outline">
        <mat-label>Number of Quarters BAS Provided</mat-label>
        <mat-select formControlName="numberOfQuarters">
          <mat-option *ngFor="let n of QUARTER_OPTIONS" [value]="n">{{ n }}</mat-option>
        </mat-select>
      </mat-form-field>
    </div>
    <div class="col-4">
      <mat-form-field appearance="outline">
        <mat-label>Most Recent BAS Held</mat-label>
        <input matInput [matDatepicker]="basDatePicker" formControlName="mostRecentBasPeriod" placeholder="Mon-YY" [max]="maxBasPeriod">
        <mat-datepicker-toggle matSuffix [for]="basDatePicker"></mat-datepicker-toggle>
        <mat-datepicker #basDatePicker startView="year"></mat-datepicker>
        <mat-error>
          <app-validation-partial [formGroup]="formGroup" [fieldName]="'mostRecentBasPeriod'"></app-validation-partial>
        </mat-error>
      </mat-form-field>
    </div>
  </div>

  <!-- Quarter grid -->
  <ng-container *ngIf="basQuartersArray.length > 0">

    <!-- Quarter column headers -->
    <div class="row bas-grid-row">
      <div class="col-3"></div>
      <div class="col text-center fw-semibold text-primary" *ngFor="let i of quarterIndices">
        Quarter {{ i + 1 }}
      </div>
    </div>

    <!-- Period row -->
    <div class="row bas-grid-row mb-2">
      <div class="col-3 text-muted small"></div>
      <div class="col text-center text-muted small fst-italic" *ngFor="let i of quarterIndices">
        {{ quarterPeriodLabels[i] || '—' }}
      </div>
    </div>

    <!-- G1 — Total Sales -->
    <div class="row align-items-center bas-grid-row">
      <div class="col-3 bas-row-label"><strong>G1</strong> &mdash; Total Sales (incl. GST)</div>
      <div class="col" *ngFor="let i of quarterIndices">
        <mat-form-field appearance="outline" class="w-100">
          <input matInput matInputCommified placeholder="Income amount" [formControl]="getQuarterControl(i, 'g1')">
          <span matPrefix>$&nbsp;</span>
          <mat-error><app-validation-partial [formGroup]="getQuarterFg(i)" [fieldName]="'g1'"></app-validation-partial></mat-error>
        </mat-form-field>
      </div>
    </div>

    <!-- Do Sales Include GST? -->
    <div class="row align-items-center bas-grid-row">
      <div class="col-3 bas-row-label">Do Sales Include GST?</div>
      <div class="col" *ngFor="let i of quarterIndices">
        <mat-form-field appearance="outline" class="w-100">
          <mat-select [formControl]="getQuarterControl(i, 'salesIncludeGst')">
            <mat-option [value]="true">Yes</mat-option>
            <mat-option [value]="false">No</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
    </div>

    <!-- 1A — Owed to ATO -->
    <div class="row align-items-center bas-grid-row">
      <div class="col-3 bas-row-label"><strong>1A</strong> &mdash; Owed to ATO</div>
      <div class="col" *ngFor="let i of quarterIndices">
        <mat-form-field appearance="outline" class="w-100">
          <input matInput matInputCommified placeholder="Income amount" [formControl]="getQuarterControl(i, 'oneA')">
          <span matPrefix>$&nbsp;</span>
          <mat-error><app-validation-partial [formGroup]="getQuarterFg(i)" [fieldName]="'oneA'"></app-validation-partial></mat-error>
        </mat-form-field>
      </div>
    </div>

    <!-- 1B — Owed by ATO -->
    <div class="row align-items-center bas-grid-row">
      <div class="col-3 bas-row-label"><strong>1B</strong> &mdash; Owed by ATO</div>
      <div class="col" *ngFor="let i of quarterIndices">
        <mat-form-field appearance="outline" class="w-100">
          <input matInput matInputCommified placeholder="Income amount" [formControl]="getQuarterControl(i, 'oneB')">
          <span matPrefix>$&nbsp;</span>
          <mat-error><app-validation-partial [formGroup]="getQuarterFg(i)" [fieldName]="'oneB'"></app-validation-partial></mat-error>
        </mat-form-field>
      </div>
    </div>

    <!-- W1 — Total Salaries -->
    <div class="row align-items-center bas-grid-row">
      <div class="col-3 bas-row-label"><strong>W1</strong> &mdash; Total Salaries</div>
      <div class="col" *ngFor="let i of quarterIndices">
        <mat-form-field appearance="outline" class="w-100">
          <input matInput matInputCommified placeholder="Income amount" [formControl]="getQuarterControl(i, 'w1')">
          <span matPrefix>$&nbsp;</span>
          <mat-error><app-validation-partial [formGroup]="getQuarterFg(i)" [fieldName]="'w1'"></app-validation-partial></mat-error>
        </mat-form-field>
      </div>
    </div>

    <!-- Salaries Attributable to Director(s) -->
    <div class="row align-items-center bas-grid-row">
      <div class="col-3 bas-row-label">Salaries attributable to the Director(s) / Applicant(s).</div>
      <div class="col" *ngFor="let i of quarterIndices">
        <mat-form-field appearance="outline" class="w-100">
          <input matInput matInputCommified placeholder="Income amount" [formControl]="getQuarterControl(i, 'directorSalaries')">
          <span matPrefix>$&nbsp;</span>
          <mat-error><app-validation-partial [formGroup]="getQuarterFg(i)" [fieldName]="'directorSalaries'"></app-validation-partial></mat-error>
        </mat-form-field>
      </div>
    </div>

    <!-- Director salaries error row -->
    <div class="row bas-grid-row">
      <div class="col-3"></div>
      <div class="col" *ngFor="let i of quarterIndices">
        <em class="text-danger small" *ngIf="getQuarterFg(i)?.errors?.['directorSalariesExceedW1']">
          ERROR: Applicant wages cannot exceed total wages for any period
        </em>
      </div>
    </div>

    <!-- Total Income for Quarter (calculated, read-only) -->
    <div class="row align-items-center bas-grid-row mb-2">
      <div class="col-3 bas-row-label fw-semibold">Total Income for Quarter</div>
      <div class="col" *ngFor="let i of quarterIndices">
        <mat-form-field appearance="outline" class="w-100">
          <input matInput matInputCommified [formControl]="getQuarterControl(i, 'quarterlyIncomeTotal')" [readonly]="true">
          <span matPrefix>$&nbsp;</span>
        </mat-form-field>
      </div>
    </div>

  </ng-container>

</div>
